import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, IconButton, Tooltip } from "@mui/material";
import { theme } from "../services/Theme";
import CloseIcon from "@mui/icons-material/Close";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useEventListener from "../services/event-listener";

function ImageModal({ modalContent, closeModal, nextImage, prevImage, contentIndex, maxIndex }) {
  const [overlay, setOverlay] = useState(true);
  const [loadedImageId, setLoadedImageId] = useState(modalContent?.id || null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState("next");
  const [previousContent, setPreviousContent] = useState(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const isMultiTouchRef = useRef(false);
  const transitionTimerRef = useRef(null);

  useEffect(() => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }

    if (isTransitioning) {
      transitionTimerRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setPreviousContent(null);
      }, 320);
    }

    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, [isTransitioning]);

  const openLink = (link) => () => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const toggleOverlay = () => {
    setOverlay((prev) => !prev);
  };

  const startTransition = (direction) => {
    setTransitionDirection(direction);
    setPreviousContent(modalContent);
    setIsTransitioning(true);
  };

  const goToPrevImage = () => {
    if (contentIndex > 0) {
      startTransition("prev");
      prevImage();
    }
  };

  const goToNextImage = () => {
    if (contentIndex < maxIndex) {
      startTransition("next");
      nextImage();
    }
  };

  const handleTouchStart = (event) => {
    isMultiTouchRef.current = event.touches.length > 1;

    if (isMultiTouchRef.current) {
      return;
    }

    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (event) => {
    if (event.touches.length > 1) {
      isMultiTouchRef.current = true;
    }
  };

  const handleTouchEnd = (event) => {
    if (isMultiTouchRef.current) {
      if (event.touches.length === 0) {
        isMultiTouchRef.current = false;
      }

      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) {
      return;
    }

    if (deltaX < 0) {
      goToNextImage();
      return;
    }

    goToPrevImage();
  };

  function handler({ key }) {
    if (String(key) === "ArrowLeft" && contentIndex > 0) {
      goToPrevImage();
    } else if (String(key) === "ArrowRight" && contentIndex < maxIndex) {
      goToNextImage();
    } else if (String(key) === "Escape") {
      closeModal();
    }
  }

  const currentImageLoaded = loadedImageId === modalContent?.id;
  const overlayVisible = overlay ? 1 : 0;

  const floatingButtonSx = {
    width: { xs: 40, sm: 46 },
    height: { xs: 40, sm: 46 },
    color: "rgba(255,255,255,0.96)",
    backgroundColor: "rgba(12,12,12,0.54)",
    backdropFilter: "blur(6px)",
    border: "1px solid rgba(255,255,255,0.24)",
    boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
    transition: "background-color 180ms ease, box-shadow 180ms ease, opacity 200ms ease",
    "&:hover": {
      backgroundColor: "rgba(18,18,18,0.72)",
      boxShadow: "0 12px 26px rgba(0,0,0,0.4)",
    },
    "&:active": {
      boxShadow: "0 9px 20px rgba(0,0,0,0.34)",
    },
  };

  useEventListener("keydown", handler);

  return (
    <Grid
      container
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "97vw", sm: "90vw", md: "800px" },
        maxHeight: "94vh",
        borderRadius: { xs: 2, sm: 3 },
        overflow: "hidden",
        bgcolor: "rgba(0,0,0,1)",
        display: "flex",
        boxShadow: "0 22px 56px rgba(0,0,0,0.55)",
      }}
    >
      <Box
        onClick={toggleOverlay}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          touchAction: "auto",
          backgroundColor: "rgba(0,0,0,1)",
        }}
      >
        {previousContent && isTransitioning && (
          <Box
            component="img"
            src={previousContent.imageKey}
            alt={previousContent.description}
            sx={{
              width: "100%",
              height: "auto",
              display: "block",
              position: "absolute",
              inset: 0,
              animation:
                transitionDirection === "next"
                  ? "modalSlideOutToLeft 300ms ease forwards"
                  : "modalSlideOutToRight 300ms ease forwards",
            }}
          />
        )}

        <Box
          component="img"
          width="100%"
          height="auto"
          id={modalContent.id}
          src={modalContent.imageKey}
          alt={modalContent.description}
          onLoad={() => setLoadedImageId(modalContent.id)}
          sx={{
            display: "block",
            position: "relative",
            maxHeight: "94vh",
            objectFit: "contain",
            opacity: currentImageLoaded ? 1 : 0,
            transition: "opacity 220ms ease",
            animation: isTransitioning
              ? transitionDirection === "next"
                ? "modalSlideInFromRight 300ms ease"
                : "modalSlideInFromLeft 300ms ease"
              : "none",
          }}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 35%, rgba(0,0,0,0.9) 100%)",
          width: "100%",
          transition: "opacity 220ms ease",
          opacity: overlayVisible,
          pointerEvents: overlay ? "auto" : "none",
          zIndex: 2,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 4, sm: 5 },
          pb: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography
          color={theme.palette.text.light}
          sx={{
            fontSize: { xs: "0.88rem", sm: "1rem" },
            lineHeight: 1.35,
            textShadow: "0 1px 4px rgba(0,0,0,0.55)",
            maxWidth: "92%",
          }}
        >
          {modalContent.description}
        </Typography>
      </Box>

      <IconButton
        onClick={closeModal}
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 14 },
          right: { xs: 10, sm: 14 },
          opacity: overlayVisible,
          pointerEvents: overlay ? "auto" : "none",
          zIndex: 3,
          ...floatingButtonSx,
        }}
      >
        <CloseIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
      </IconButton>

      <Box
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 14 },
          left: { xs: 10, sm: 14 },
          display: "flex",
          gap: { xs: 1, sm: 1.25 },
          opacity: overlayVisible,
          pointerEvents: overlay ? "auto" : "none",
          zIndex: 3,
        }}
      >
        <Tooltip title="View Post on Facebook">
          <IconButton onClick={openLink(modalContent.facebookLink)} sx={floatingButtonSx}>
            <FacebookIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="View Post on Instagram">
          <IconButton onClick={openLink(modalContent.instagramLink)} sx={floatingButtonSx}>
            <InstagramIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: { xs: 14, sm: 18 },
          left: "50%",
          transform: "translateX(-50%)",
          px: { xs: 1.2, sm: 1.8 },
          py: { xs: 0.35, sm: 0.45 },
          borderRadius: 20,
          backgroundColor: "rgba(6,6,6,0.5)",
          backdropFilter: "blur(4px)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "rgba(255,255,255,0.95)",
          pointerEvents: "none",
          opacity: overlayVisible,
          transition: "opacity 220ms ease",
          zIndex: 3,
        }}
      >
        <Typography sx={{ fontSize: { xs: "0.74rem", sm: "0.82rem" }, fontWeight: 600 }}>
          {contentIndex + 1} / {maxIndex + 1}
        </Typography>
      </Box>

      {contentIndex > 0 && (
        <IconButton
          onClick={goToPrevImage}
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: 8, sm: 14 },
            transform: "translateY(-50%)",
            opacity: overlayVisible,
            pointerEvents: overlay ? "auto" : "none",
            zIndex: 3,
            ...floatingButtonSx,
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: { xs: 24, sm: 30 } }} />
        </IconButton>
      )}

      {contentIndex < maxIndex && (
        <IconButton
          onClick={goToNextImage}
          sx={{
            position: "absolute",
            top: "50%",
            right: { xs: 8, sm: 14 },
            transform: "translateY(-50%)",
            opacity: overlayVisible,
            pointerEvents: overlay ? "auto" : "none",
            zIndex: 3,
            ...floatingButtonSx,
          }}
        >
          <ChevronRightIcon sx={{ fontSize: { xs: 24, sm: 30 } }} />
        </IconButton>
      )}

      <style>
        {`@keyframes modalSlideInFromRight {
            from { transform: translateX(12%); opacity: 0.2; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes modalSlideInFromLeft {
            from { transform: translateX(-12%); opacity: 0.2; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes modalSlideOutToLeft {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(-12%); opacity: 0; }
          }
          @keyframes modalSlideOutToRight {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(12%); opacity: 0; }
          }`}
      </style>
    </Grid>
  );
}

export default ImageModal;
